import React, { useRef, useEffect } from 'react';
import TextField from '@mui/material/TextField';

/**
 * A dynamic text field that adjusts its height to fit the content.
 * Automatically sets its height to fit the content without the need to set
 * a fixed height. This is useful for text fields that need to be in a
 * scrollable area.
 *
 * @param {bool} isReadOnly - Whether the text field is read-only.
 * @param {string} label - The label for the text field.
 * @param {number} rowsMax - The maximum number of rows the text field should
 *   grow to.
 * @param {string} value - The value of the text field.
 * @param {function} onChange - The function to call when the text field value
 *   changes. The new value is passed as an argument.
 * @param {object} rest - Additional props to pass to the underlying TextField
 *   component.
 * @returns {ReactElement} The dynamic text field component.
 */
const DynamicTextField = ({ isReadOnly, label, rowsMax, value, onChange, ...rest }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const textarea = inputRef.current;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value]);

  /**
   * Handles changes to the text field input.
   * 
   * @param {object} event - The event object from the input change.
   * Calls the onChange prop with the new value of the input.
   */

  const handleOnChange = (event) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <TextField
      multiline
      size='small'
      rows={1}
      rowsMax={rowsMax}
      label={label}
      InputProps={{
        inputComponent: 'textarea',
        inputProps: {
          ref: inputRef,
          style: { overflow: 'hidden', resize: 'none' },
          onChange: handleOnChange,
          readOnly: isReadOnly
        },
      }}
      value={value}
      disabled={isReadOnly}
      {...rest}
    />
  );
};

export default DynamicTextField;
